/**
 * Indicates if an HTML element has the specified CSS class.
 *
 * @param ele HTML element
 * @param cls Class name
 * @return True if the element has the specified class.
 */
export function hasClass(ele: HTMLElement, cls: string): boolean {
  // Adapted from http://stackoverflow.com/questions/2155737/remove-css-class-from-element-with-javascript-no-jquery
  return new RegExp(`(\\s|^)${cls}(\\s|$)`).test(ele.className);
}

/**
 * Removes the specified CSS class from the HTML element, if needed.
 *
 * @param ele HTML element
 * @param cls Class name
 */
export function removeClass(ele: HTMLElement, cls: string): void {
  // Adapted from http://stackoverflow.com/questions/2155737/remove-css-class-from-element-with-javascript-no-jquery
  if (hasClass(ele, cls)) {
    const reg = new RegExp(`(\\s|^)${cls}(\\s|$)`);
    // eslint-disable-next-line no-param-reassign
    ele.className = ele.className.replace(reg, " ").trim();
  }
}

/**
 * Adds the specified CSS class to the HTML element, if needed.
 *
 * @param ele HTML element
 * @param cls Class name
 * @return The modified HTML element.
 */
export function addClass(ele: HTMLElement, cls: string): HTMLElement {
  // Adapted from http://stackoverflow.com/questions/2155737/remove-css-class-from-element-with-javascript-no-jquery
  if (ele.classList) {
    ele.classList.add(cls);
  } else if (!hasClass(ele, cls)) {
    const classes = ele.className.split(" ");
    classes.push(cls);
    // eslint-disable-next-line no-param-reassign
    ele.className = classes.join(" ");
  }
  return ele;
}

/**
 * Finds the element matching selector at the horizontal middle of root,
 * using optional overrideX/overrideY coordinates.
 */
export function findMiddleNode(
  root: HTMLElement,
  selector: string,
  options?: { overrideX?: number; overrideY?: number }
): HTMLElement | null {
  // Measure root bounds
  const rootRect = root.getBoundingClientRect();
  // Determine hit-test coordinates
  const centerX = options?.overrideX ?? rootRect.left + rootRect.width / 2;
  const centerY = options?.overrideY ?? rootRect.top + rootRect.height / 2;
  // Hit-test at computed point
  const target = document.elementFromPoint(
    centerX,
    centerY
  ) as HTMLElement | null;
  return target?.closest(selector) ?? null;
}

export const createElement = (tagName: string) => {
  return document.createElement(tagName);
};

export const appendScriptToHead = (script: HTMLScriptElement) => {
  document.head.appendChild(script);
};
